import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MemberReferralTypeClient, MemberReferralTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberReferralTypeClient(API_BASE_URL, axiosInstance);

// Cached query for dropdowns
export const useMemberReferralTypes = () => {
    return useQuery({
        queryKey: ['memberReferralTypes'],
        queryFn: async () => {
            const response = await apiClient.searchMemberReferralTypes({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'DisplayPosition',
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

// Uncached search for admin tables
export const useMemberReferralTypeSearch = () => {
    const search = async (params: any) => {
        const response = await apiClient.searchMemberReferralTypes(params);
        return {
            results: response?.data?.results || [],
            totalCount: response?.data?.totalCount || 0,
        };
    };

    return { search };
};

// Query by ID
export const useMemberReferralTypeById = (id: string) => {
    return useQuery({
        queryKey: ['memberReferralType', id],
        queryFn: async () => {
            const response = await apiClient.getMemberReferralTypeById(id);
            return response?.data || null;
        },
        enabled: !!id,
    });
};

// Create mutation
export const useCreateMemberReferralType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: MemberReferralTypeDTO) => {
            return await apiClient.createMemberReferralType(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberReferralTypes'] });
        },
    });
};

// Update mutation
export const useUpdateMemberReferralType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: MemberReferralTypeDTO }) => {
            return await apiClient.updateMemberReferralType(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberReferralTypes'] });
        },
    });
};

// Delete mutation
export const useDeleteMemberReferralType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return await apiClient.deleteMemberReferralType(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberReferralTypes'] });
        },
    });
};